import React, { useState, useEffect } from 'react';
import {
  FaCoins,
  FaLeaf,
  FaHeart,
  FaComments,
  FaTasks,
  FaEye,
  FaHandsHelping,
  FaCheckCircle,
  FaUserPlus,
} from 'react-icons/fa';

/**
 * トークン増加時のアニメーションエフェクトコンポーネント
 * 8つの森の生態系に対応したビジュアルエフェクト
 */
const TokenAnimationEffect = ({
  amount,
  tokenType = 'SHINKETSU',
  ecosystemCategory,
  position = { x: 0, y: 0 },
  onComplete,
  show = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('initial');

  // 生態系別のアイコンと色設定
  const ecosystemConfig = {
    QT: {
      icon: <FaLeaf />,
      color: 'text-green-500',
      bgColor: 'bg-green-100',
      name: '問いの森',
      particle: '🌱',
    },
    ET: {
      icon: <FaHeart />,
      color: 'text-red-500',
      bgColor: 'bg-red-100',
      name: '共鳴林',
      particle: '💖',
    },
    DT: {
      icon: <FaComments />,
      color: 'text-blue-500',
      bgColor: 'bg-blue-100',
      name: '議論の森',
      particle: '💬',
    },
    XT: {
      icon: <FaTasks />,
      color: 'text-orange-500',
      bgColor: 'bg-orange-100',
      name: '実行の森',
      particle: '⚡',
    },
    RT: {
      icon: <FaEye />,
      color: 'text-purple-500',
      bgColor: 'bg-purple-100',
      name: '振り返りの森',
      particle: '🔍',
    },
    CT: {
      icon: <FaHandsHelping />,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-100',
      name: '支援の森',
      particle: '🤝',
    },
    TT: {
      icon: <FaCheckCircle />,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-100',
      name: '信任の森',
      particle: '⭐',
    },
    IT: {
      icon: <FaUserPlus />,
      color: 'text-pink-500',
      bgColor: 'bg-pink-100',
      name: '招待の森',
      particle: '👋',
    },
  };

  const config = ecosystemConfig[ecosystemCategory] || {
    icon: <FaCoins />,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-100',
    name: 'SHINKETSU',
    particle: '🪙',
  };

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setAnimationPhase('appear');

      // アニメーション段階の制御
      const timers = [
        setTimeout(() => setAnimationPhase('rise'), 100),
        setTimeout(() => setAnimationPhase('peak'), 800),
        setTimeout(() => setAnimationPhase('fade'), 1200),
        setTimeout(() => {
          setIsVisible(false);
          onComplete?.();
        }, 1800),
      ];

      return () => timers.forEach(clearTimeout);
    }
  }, [show, onComplete]);

  // パーティクル生成
  const generateParticles = () => {
    return Array.from({ length: 8 }, (_, i) => (
      <div
        key={i}
        className={`absolute text-lg animate-bounce ${config.color}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 0.5}s`,
          animationDuration: '1s',
        }}
      >
        {config.particle}
      </div>
    ));
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -50%)',
      }}
    >
      {/* メインエフェクト */}
      <div
        className={`
          relative flex items-center justify-center
          transition-all duration-700 ease-out
          ${animationPhase === 'appear' ? 'scale-0 opacity-0' : ''}
          ${animationPhase === 'rise' ? 'scale-110 opacity-100 -translate-y-4' : ''}
          ${animationPhase === 'peak' ? 'scale-125 opacity-100 -translate-y-8' : ''}
          ${animationPhase === 'fade' ? 'scale-90 opacity-0 -translate-y-12' : ''}
        `}
      >
        {/* グロー効果 */}
        <div
          className={`
            absolute inset-0 rounded-full blur-lg
            ${config.bgColor} opacity-60
            animate-ping
          `}
          style={{ width: '120px', height: '120px' }}
        />

        {/* メインカード */}
        <div
          className={`
          relative z-10 flex flex-col items-center justify-center
          bg-white rounded-2xl shadow-2xl border-2 
          ${config.bgColor} border-opacity-30
          px-6 py-4 min-w-[160px]
        `}
        >
          {/* 生態系アイコン */}
          <div className={`text-3xl ${config.color} mb-2`}>{config.icon}</div>

          {/* 金額表示 */}
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-800 mb-1">
              +{amount}
            </div>
            <div className="text-sm font-medium text-gray-600">{tokenType}</div>
            {ecosystemCategory && (
              <div className={`text-xs ${config.color} font-medium`}>
                {config.name}
              </div>
            )}
          </div>

          {/* キラキラエフェクト */}
          <div className="absolute inset-0 overflow-hidden rounded-2xl">
            <div className="absolute top-2 left-2 text-yellow-300 animate-pulse">
              ✨
            </div>
            <div
              className="absolute top-1 right-3 text-yellow-200 animate-pulse"
              style={{ animationDelay: '0.3s' }}
            >
              ⭐
            </div>
            <div
              className="absolute bottom-2 left-3 text-yellow-400 animate-pulse"
              style={{ animationDelay: '0.6s' }}
            >
              💫
            </div>
            <div
              className="absolute bottom-1 right-2 text-yellow-300 animate-pulse"
              style={{ animationDelay: '0.9s' }}
            >
              ✨
            </div>
          </div>
        </div>

        {/* パーティクル効果 */}
        <div className="absolute inset-0 w-32 h-32 overflow-hidden">
          {generateParticles()}
        </div>

        {/* リップルエフェクト */}
        <div
          className={`
          absolute inset-0 rounded-full border-4 
          ${config.color} border-opacity-30
          animate-ping
        `}
          style={{
            width: '140px',
            height: '140px',
            animationDuration: '1.5s',
          }}
        />
      </div>
    </div>
  );
};

/**
 * 複数のトークンアニメーション管理フック
 */
export const useTokenAnimation = () => {
  const [animations, setAnimations] = useState([]);

  const triggerAnimation = (config) => {
    const id = Date.now() + Math.random();
    const newAnimation = {
      id,
      ...config,
      show: true,
    };

    setAnimations((prev) => [...prev, newAnimation]);

    // アニメーション完了後に削除
    setTimeout(() => {
      setAnimations((prev) => prev.filter((anim) => anim.id !== id));
    }, 2000);
  };

  const AnimationRenderer = () => (
    <>
      {animations.map((animation) => (
        <TokenAnimationEffect
          key={animation.id}
          {...animation}
          onComplete={() => {
            setAnimations((prev) =>
              prev.filter((anim) => anim.id !== animation.id)
            );
          }}
        />
      ))}
    </>
  );

  return { triggerAnimation, AnimationRenderer };
};

/**
 * 画面上の要素位置を取得するユーティリティ
 */
export const getElementPosition = (element) => {
  if (!element) return { x: window.innerWidth / 2, y: window.innerHeight / 2 };

  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
};

/**
 * 生態系別の使用例コンポーネント（デモ用）
 */
export const TokenAnimationDemo = () => {
  const { triggerAnimation, AnimationRenderer } = useTokenAnimation();
  const ecosystems = ['QT', 'ET', 'DT', 'XT', 'RT', 'CT', 'TT', 'IT'];

  const handleDemoClick = (ecosystem) => {
    const amounts = [2, 5, 10, 15, 20, 50, 100];
    const randomAmount = amounts[Math.floor(Math.random() * amounts.length)];

    triggerAnimation({
      amount: randomAmount,
      tokenType: 'SHINKETSU',
      ecosystemCategory: ecosystem,
      position: {
        x: Math.random() * (window.innerWidth - 200) + 100,
        y: Math.random() * (window.innerHeight - 200) + 100,
      },
    });
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-8">
          トークンアニメーション デモ
        </h2>

        <div className="grid grid-cols-4 gap-4 mb-8">
          {ecosystems.map((ecosystem) => (
            <button
              key={ecosystem}
              onClick={() => handleDemoClick(ecosystem)}
              className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            >
              <div className="text-center">
                <div className="text-2xl mb-2">
                  {ecosystem === 'QT' && '🌱'}
                  {ecosystem === 'ET' && '💖'}
                  {ecosystem === 'DT' && '💬'}
                  {ecosystem === 'XT' && '⚡'}
                  {ecosystem === 'RT' && '🔍'}
                  {ecosystem === 'CT' && '🤝'}
                  {ecosystem === 'TT' && '⭐'}
                  {ecosystem === 'IT' && '👋'}
                </div>
                <div className="font-semibold text-sm">{ecosystem}</div>
              </div>
            </button>
          ))}
        </div>

        <p className="text-center text-gray-600">
          上のボタンをクリックして各生態系のアニメーションを確認できます
        </p>
      </div>

      <AnimationRenderer />
    </div>
  );
};

export default TokenAnimationEffect;
